import React, { useState, useEffect, useRef } from 'react';
import { supabase } from './supabase';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

const TIPOS_PROPIEDAD = {
  'Residencial': ['Casa', 'Casa en condominio', 'Departamento', 'Habitación', 'Quinta', 'Rancho', 'Terreno', 'Villa'],
  'Comercial': ['Bodega comercial', 'Casa con uso de suelo', 'Edificio', 'Huerta', 'Local comercial', 'Local en centro comercial', 'Oficina', 'Terreno comercial'],
  'Industrial': ['Bodega industrial', 'Nave industrial', 'Terreno industrial'],
};
const TODOS_TIPOS = Object.values(TIPOS_PROPIEDAD).flat();

const CARACTERISTICAS = {
  'Amenidades': ['Estacionamiento de visitas'],
  'Exterior': ['Acceso a la playa', 'Andén', 'Balcón', 'Cisterna', 'Estacionamiento techado', 'Facilidad para estacionarse', 'Frente a la playa', 'Frente al agua', 'Garaje', 'Jardín', 'Muelle de carga', 'Parrilla', 'Patio', 'Riego por aspersión', 'Roof garden', 'Terraza', 'Vista al agua', 'Vista al mar'],
  'General': ['Accesible para adultos mayores', 'Accesible para personas con discapacidad', 'Aire acondicionado', 'Alarma', 'Amueblado', 'Bodega', 'Calefacción', 'Chimenea', 'Circuito cerrado', 'Clóset', 'Cocina', 'Cocina equipada', 'Cocina integral', 'Conexión para lavadora', 'Conmutador', 'Cuarto de servicio', 'Dormitorio principal', 'Dos plantas', 'Elevador', 'Estudio', 'Fraccionamiento privado', 'Hidroneumático', 'Oficina', 'Panel solar', 'Penthouse', 'Planta Baja', 'Planta eléctrica', 'Portero', 'Rampas', 'Recámara en planta baja', 'Seguridad 12 horas', 'Seguridad 24 horas', 'Una sola planta', 'Vestidor'],
  'Políticas': ['Mascotas permitidas', 'No se aceptan mascotas', 'Permitido fumar', 'Prohibido fumar'],
  'Recreación': ['Alberca', 'Área de juegos infantiles', 'Cancha de pádel', 'Cancha de tenis', 'Cine', 'Fogatero', 'Gimnasio', 'Jacuzzi', 'Ludoteca', 'Salón de usos múltiples', 'Sauna'],
};

const OPCIONES_NUM = ['0','1','1.5','2','2.5','3','3.5','4','4.5','5+'];
const OPCIONES_ANIO = ['A estrenar','Menos de 1 año','1 a 5 años','5 a 10 años','10 a 20 años','Más de 20 años'];
const OPCIONES_CONDICION = ['Nuevo','Excelente estado','Buen estado','Regular','Para remodelar'];
const OPCIONES_DISPOSICION = ['Frente','Contrafrente','Interno'];
const OPCIONES_ORIENTACION = ['Norte','Sur','Este','Oeste','Norte-Sur','Este-Oeste'];
const ESTADOS_MX = ['Jalisco','Ciudad de México','Nuevo León','Estado de México','Querétaro','Guanajuato','Puebla','Otro'];
const MAX_FOTOS = 20;

function useIsMobile() {
  const [isMobile, setIsMobile] = React.useState(window.innerWidth < 768);
  React.useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);
  return isMobile;
}

const fmt = (n) => n ? `$${Number(n).toLocaleString('es-MX', { maximumFractionDigits: 0 })}` : '';

// FIX: campos que se guardan como columnas `numeric` en Supabase (todos los
// que usan <input type='number'> en el formulario). Cuando el campo queda
// vacío, form[key] es '' — y Postgres rechaza '' para numeric con el error
// "invalid input syntax for type numeric". Se normalizan a null antes de
// guardar, igual que ya se hace con "presupuesto" en Contactos.
const CAMPOS_NUMERICOS = [
  'precio_venta', 'precio_renta', 'precio_renta_temporal',
  'construccion_m2', 'superficie_no_cubierta_m2', 'terreno_m2',
  'frente_terreno', 'fondo_terreno', 'mantenimiento_monto',
  'latitud', 'longitud',
  // FIX: estos también resultaron ser columnas numéricas en la base —
  // vienen de un <select> con opciones tipo "5+" (texto, no número), así
  // que además de convertir '' a null hay que traducir "5+" a 5.
  'recamaras', 'banos', 'medios_banos', 'estacionamientos',
];

export default function Corretaje({ miRol, miAgente }) {
  const isMobile = useIsMobile();
  const [propiedades, setPropiedades] = useState([]);
  const [loading, setLoading] = useState(true);
  const [buscar, setBuscar] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editando, setEditando] = useState(null);
  const [form, setForm] = useState(formVacio());
  const [tabForm, setTabForm] = useState('general');
  const [guardando, setGuardando] = useState(false);
  const [showFiltros, setShowFiltros] = useState(false);
  const [filtroTipo, setFiltroTipo] = useState([]);
  const [filtroOperacion, setFiltroOperacion] = useState('');
  const [filtroPrecioMin, setFiltroPrecioMin] = useState('');
  const [filtroPrecioMax, setFiltroPrecioMax] = useState('');
  const [filtroCiudad, setFiltroCiudad] = useState('');
  const [propiedadCotizar, setPropiedadCotizar] = useState(null);
  const [generandoPDF, setGenerandoPDF] = useState(false);
  const [subiendoFoto, setSubiendoFoto] = useState(false);
  const [asesor, setAsesor] = useState({ nombre: '', celular: '', email: '' });
  const cotizacionRef = useRef();
  const galeriaRefs = useRef([]);

  const puedeAgregar = miRol === 'Super Admin' || miRol === 'Admin' || (miAgente?.corretaje_encargado && (miRol === 'Gerente Editor' || miRol === 'Gerente Operador'));

  function formVacio() {
    return {
      tipo_propiedad: '', titulo: '', descripcion: '',
      venta_activa: false, precio_venta: '', renta_activa: false, precio_renta: '',
      renta_temporal_activa: false, precio_renta_temporal: '', moneda: 'MXN', mostrar_precio: true,
      recamaras: '', banos: '', medios_banos: '', estacionamientos: '',
      construccion_m2: '', superficie_no_cubierta_m2: '', terreno_m2: '', terreno_unidad: 'm²',
      fondo_terreno: '', frente_terreno: '', anio_construccion: '', piso: '', pisos_edificio: '',
      condicion: '', disposicion: '', orientacion: '', mantenimiento_monto: '', mantenimiento_incluido: 'No indicado',
      clave_interna: '', codigo_llave: '',
      mostrar_ubicacion_exacta: true, calle: '', numero: '', interior: '', esquina_con: '',
      codigo_postal: '', pais: 'México', estado: 'Jalisco', ciudad: '', colonia: '',
      latitud: '', longitud: '',
      caracteristicas: [], portada_url: '', fotos: [], video_url: '', tour_virtual_url: '', activo: true,
    };
  }

  useEffect(() => { cargarPropiedades(); cargarAsesor(); }, []);

  const cargarPropiedades = async () => {
    setLoading(true);
    const { data } = await supabase.from('corretaje_propiedades').select('*').eq('activo', true).order('created_at', { ascending: false });
    setPropiedades(data || []);
    setLoading(false);
  };

  const cargarAsesor = async () => {
    if (miAgente) setAsesor({ nombre: `${miAgente.nombre || ''} ${miAgente.apellidos || ''}`.trim(), celular: miAgente.telefono || '', email: miAgente.correo || '' });
  };

  const handleGuardar = async () => {
    if (!form.titulo.trim() || !form.tipo_propiedad) { alert('Falta el tipo de propiedad o el título'); return; }
    setGuardando(true);
    const payload = { ...form, updated_at: new Date().toISOString() };
    // FIX: "5+" es una opción de texto en el dropdown, no un número válido
    // — se traduce a 5 antes de normalizar los vacíos.
    ['recamaras', 'banos', 'medios_banos', 'estacionamientos'].forEach(k => {
      if (payload[k] === '5+') payload[k] = 5;
    });
    // FIX: normaliza los campos numéricos vacíos ('') a null antes de
    // guardar — Postgres rechaza '' para columnas numeric.
    CAMPOS_NUMERICOS.forEach(k => {
      if (payload[k] === '' || payload[k] === undefined) payload[k] = null;
    });
    delete payload.folio; // el folio nunca se edita, se genera solo
    if (!editando) payload.creado_por = miAgente?.correo || '';

    let error;
    if (editando) {
      ({ error } = await supabase.from('corretaje_propiedades').update(payload).eq('id', editando));
    } else {
      ({ error } = await supabase.from('corretaje_propiedades').insert([payload]));
    }
    setGuardando(false);
    if (error) { alert('Error al guardar: ' + error.message); return; }
    setShowForm(false); setEditando(null); setForm(formVacio()); setTabForm('general');
    cargarPropiedades();
  };

  const handleEditar = (p) => {
    setForm({ ...formVacio(), ...p, caracteristicas: p.caracteristicas || [], fotos: p.fotos || [] });
    setEditando(p.id); setShowForm(true); setTabForm('general');
  };

  const handleEliminar = async (id) => {
    if (!window.confirm('¿Eliminar esta propiedad?')) return;
    await supabase.from('corretaje_propiedades').update({ activo: false }).eq('id', id);
    cargarPropiedades();
  };

  const handleToggleCaracteristica = (c) => {
    setForm(f => ({ ...f, caracteristicas: f.caracteristicas.includes(c) ? f.caracteristicas.filter(x => x !== c) : [...f.caracteristicas, c] }));
  };

  const handleSubirPortada = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setSubiendoFoto(true);
    const path = `${editando || 'nuevo'}_portada_${Date.now()}.${file.name.split('.').pop()}`;
    const { error } = await supabase.storage.from('corretaje').upload(path, file, { upsert: true });
    if (!error) {
      const { data: { publicUrl } } = supabase.storage.from('corretaje').getPublicUrl(path);
      setForm(f => ({ ...f, portada_url: publicUrl }));
    }
    setSubiendoFoto(false);
  };

  const handleSubirFoto = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (form.fotos.length >= MAX_FOTOS) { alert(`Máximo ${MAX_FOTOS} fotos`); return; }
    setSubiendoFoto(true);
    const path = `${editando || 'nuevo'}_foto_${Date.now()}.${file.name.split('.').pop()}`;
    const { error } = await supabase.storage.from('corretaje').upload(path, file, { upsert: true });
    if (!error) {
      const { data: { publicUrl } } = supabase.storage.from('corretaje').getPublicUrl(path);
      setForm(f => ({ ...f, fotos: [...f.fotos, { url: publicUrl, descripcion: '' }] }));
    }
    setSubiendoFoto(false);
  };

  const handleQuitarFoto = (i) => setForm(f => ({ ...f, fotos: f.fotos.filter((_, idx) => idx !== i) }));
  const handleDescripcionFoto = (i, texto) => setForm(f => ({ ...f, fotos: f.fotos.map((foto, idx) => idx === i ? { ...foto, descripcion: texto } : foto) }));

  // ============ Filtros ============

  const propiedadesFiltradas = propiedades.filter(p => {
    const textOk = !buscar || p.titulo?.toLowerCase().includes(buscar.toLowerCase()) || p.folio?.toLowerCase().includes(buscar.toLowerCase()) || p.colonia?.toLowerCase().includes(buscar.toLowerCase());
    const tipoOk = filtroTipo.length === 0 || filtroTipo.includes(p.tipo_propiedad);
    const ciudadOk = !filtroCiudad || p.ciudad?.toLowerCase().includes(filtroCiudad.toLowerCase());
    let precioOk = true;
    if (filtroOperacion) {
      const campoPrecio = filtroOperacion === 'Venta' ? p.precio_venta : filtroOperacion === 'Renta' ? p.precio_renta : p.precio_renta_temporal;
      const campoActivo = filtroOperacion === 'Venta' ? p.venta_activa : filtroOperacion === 'Renta' ? p.renta_activa : p.renta_temporal_activa;
      if (!campoActivo) precioOk = false;
      else {
        if (filtroPrecioMin && Number(campoPrecio) < Number(filtroPrecioMin)) precioOk = false;
        if (filtroPrecioMax && Number(campoPrecio) > Number(filtroPrecioMax)) precioOk = false;
      }
    }
    return textOk && tipoOk && ciudadOk && precioOk;
  });

  const toggleFiltroTipo = (t) => setFiltroTipo(prev => prev.includes(t) ? prev.filter(x => x !== t) : [...prev, t]);

  // ============ PDF de cotización ============

  const generarPDF = async () => {
    setGenerandoPDF(true);
    const p = propiedadCotizar;
    try {
      const doc = new jsPDF('p', 'mm', 'letter');
      const pageW = doc.internal.pageSize.getWidth();
      const pageH = doc.internal.pageSize.getHeight();

      // Página 1: ficha principal
      await new Promise(r => setTimeout(r, 150));
      const canvas1 = await html2canvas(cotizacionRef.current, { scale: 2, useCORS: true, backgroundColor: '#ffffff' });
      doc.addImage(canvas1.toDataURL('image/png'), 'PNG', 0, 0, pageW, pageH);

      // Páginas siguientes: galería de fotos, 6 por página (ya renderizadas ocultas)
      for (let i = 0; i < galeriaRefs.current.length; i++) {
        const el = galeriaRefs.current[i];
        if (!el) continue;
        doc.addPage();
        const canvas = await html2canvas(el, { scale: 2, useCORS: true, backgroundColor: '#ffffff' });
        doc.addImage(canvas.toDataURL('image/png'), 'PNG', 0, 0, pageW, pageH);
      }

      doc.save(`${p.folio}_${p.titulo?.replace(/\s/g, '_').slice(0, 40)}.pdf`);
    } catch (err) {
      alert('Error al generar el PDF: ' + err.message);
    }
    setGenerandoPDF(false);
  };

  const fechaActual = new Date().toLocaleString('es-MX', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });

  // ============ Render: ficha de cotización (oculta, para capturar a PDF) ============

  const renderFichaCotizacion = () => {
    if (!propiedadCotizar) return null;
    const p = propiedadCotizar;
    const operaciones = [];
    if (p.venta_activa) operaciones.push(`${fmt(p.precio_venta)} EN VENTA`);
    if (p.renta_activa) operaciones.push(`${fmt(p.precio_renta)}/mes EN RENTA`);
    if (p.renta_temporal_activa) operaciones.push(`${fmt(p.precio_renta_temporal)} RENTA TEMPORAL`);
    const fotosGaleria = (p.fotos || []).filter(f => f.url);
    const gruposGaleria = [];
    for (let i = 0; i < fotosGaleria.length; i += 6) gruposGaleria.push(fotosGaleria.slice(i, i + 6));

    return (
      <div style={{ position: 'fixed', left: '-9999px', top: 0 }}>
        <div ref={cotizacionRef} style={{ width: '816px', minHeight: '1056px', background: '#fff', padding: '36px', boxSizing: 'border-box', fontFamily: 'Arial, sans-serif' }}>
          <div style={{ textAlign: 'center', marginBottom: '16px' }}>
            <div style={{ fontSize: '22px', fontWeight: '900', color: '#1a1a2e', letterSpacing: '3px' }}>BLACK SHEEP</div>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', borderBottom: '2px solid #1a1a2e', paddingBottom: '10px', marginBottom: '14px' }}>
            <div>
              <div style={{ fontSize: '11px', color: '#888' }}>{p.colonia}{p.colonia && p.ciudad ? ', ' : ''}{p.ciudad}</div>
              <div style={{ fontSize: '16px', fontWeight: '700', color: '#1a1a2e', maxWidth: '480px' }}>{p.titulo}</div>
            </div>
            <div style={{ textAlign: 'right' }}>
              {operaciones.map((o, i) => <div key={i} style={{ fontSize: i === 0 ? '16px' : '12px', fontWeight: '700', color: '#1a1a2e' }}>{o}</div>)}
              <div style={{ fontSize: '10px', color: '#aaa', marginTop: '2px' }}>{p.folio}</div>
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginBottom: '14px' }}>
            {p.portada_url && <img src={p.portada_url} alt="portada" style={{ width: '100%', height: '220px', objectFit: 'cover', borderRadius: '4px' }} crossOrigin="anonymous" />}
            {fotosGaleria[0]?.url && <img src={fotosGaleria[0].url} alt="foto" style={{ width: '100%', height: '220px', objectFit: 'cover', borderRadius: '4px' }} crossOrigin="anonymous" />}
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: '4px', textAlign: 'center', marginBottom: '14px', paddingBottom: '12px', borderBottom: '1px solid #ddd' }}>
            {[
              [p.recamaras, 'Recámaras'], [p.banos, 'Baños'], [p.medios_banos, 'Medios baños'],
              [p.estacionamientos, 'Estacionamientos'], [p.construccion_m2 ? `${p.construccion_m2} m²` : '', 'Construcción'], [p.terreno_m2 ? `${p.terreno_m2} m²` : '', 'Terreno'],
            ].map(([val, label], i) => val ? (
              <div key={i}><div style={{ fontSize: '14px', fontWeight: '700', color: '#1a1a2e' }}>{val}</div><div style={{ fontSize: '9px', color: '#888' }}>{label}</div></div>
            ) : <div key={i} />)}
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
            <div>
              <div style={{ fontSize: '12px', fontWeight: '700', color: '#1a1a2e', marginBottom: '6px' }}>Descripción</div>
              <div style={{ fontSize: '10px', color: '#444', lineHeight: '1.6', whiteSpace: 'pre-line' }}>{p.descripcion}</div>
            </div>
            <div>
              <div style={{ fontSize: '12px', fontWeight: '700', color: '#1a1a2e', marginBottom: '6px' }}>Amenidades</div>
              {(p.caracteristicas || []).map((c, i) => <div key={i} style={{ fontSize: '10px', color: '#444', marginBottom: '2px' }}>· {c}</div>)}
            </div>
          </div>

          <div style={{ position: 'absolute', bottom: '30px', left: '36px', right: '36px', fontSize: '9px', color: '#888', borderTop: '1px solid #ddd', paddingTop: '8px' }}>
            Asesor: {asesor.nombre} | {asesor.celular} | {asesor.email} — {fechaActual}
          </div>
        </div>

        {gruposGaleria.map((grupo, gi) => (
          <div key={gi} ref={el => galeriaRefs.current[gi] = el} style={{ width: '816px', minHeight: '1056px', background: '#fff', padding: '36px', boxSizing: 'border-box' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
              {grupo.map((foto, fi) => (
                <div key={fi}>
                  <img src={foto.url} alt="" style={{ width: '100%', height: '220px', objectFit: 'cover', borderRadius: '4px' }} crossOrigin="anonymous" />
                  {foto.descripcion && <div style={{ fontSize: '9px', color: '#888', marginTop: '4px' }}>{foto.descripcion}</div>}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    );
  };

  // ============ UI ============

  const inp = (label, key, type = 'text', extra = {}) => (
    <div style={{ marginBottom: '14px' }}>
      <label style={{ fontSize: '11px', color: '#888', display: 'block', marginBottom: '4px' }}>{label}</label>
      <input type={type} value={form[key] ?? ''} onChange={e => setForm({ ...form, [key]: e.target.value })}
        style={{ width: '100%', padding: '9px 10px', border: '0.5px solid #ddd', borderRadius: '6px', fontSize: '13px', boxSizing: 'border-box' }} {...extra} />
    </div>
  );

  const sel = (label, key, opciones) => (
    <div style={{ marginBottom: '14px' }}>
      <label style={{ fontSize: '11px', color: '#888', display: 'block', marginBottom: '4px' }}>{label}</label>
      <select value={form[key] ?? ''} onChange={e => setForm({ ...form, [key]: e.target.value })}
        style={{ width: '100%', padding: '9px 10px', border: '0.5px solid #ddd', borderRadius: '6px', fontSize: '13px', background: '#fff' }}>
        <option value=''>No indicado</option>
        {opciones.map(o => <option key={o} value={o}>{o}</option>)}
      </select>
    </div>
  );

  if (propiedadCotizar) {
    return (
      <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.85)', zIndex: 2000, overflowY: 'auto', padding: '1rem' }}>
        {renderFichaCotizacion()}
        <div style={{ maxWidth: '500px', margin: '4rem auto', background: '#fff', borderRadius: '12px', padding: '2rem', textAlign: 'center' }}>
          <div style={{ fontSize: '16px', fontWeight: '600', marginBottom: '8px' }}>{propiedadCotizar.titulo}</div>
          <div style={{ fontSize: '13px', color: '#888', marginBottom: '1.5rem' }}>{propiedadCotizar.folio}</div>
          <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
            <button onClick={generarPDF} disabled={generandoPDF} style={{ padding: '12px 24px', background: '#4A4A4A', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '14px', cursor: 'pointer' }}>
              {generandoPDF ? 'Generando...' : '⬇ Descargar ficha PDF'}
            </button>
            <button onClick={() => setPropiedadCotizar(null)} style={{ padding: '12px 24px', background: '#f5f5f5', color: '#333', border: 'none', borderRadius: '8px', fontSize: '14px', cursor: 'pointer' }}>
              Cerrar
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: isMobile ? '1rem' : '2rem' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '10px' }}>
        <div>
          <h2 style={{ fontSize: isMobile ? '17px' : '20px', fontWeight: '500', color: '#1a1a2e', marginBottom: '4px' }}>Corretaje</h2>
          <div style={{ fontSize: '13px', color: '#888' }}>{propiedadesFiltradas.length} propiedades</div>
        </div>
        {puedeAgregar && (
          <button onClick={() => { setForm(formVacio()); setEditando(null); setShowForm(true); setTabForm('general'); }}
            style={{ background: '#4A4A4A', color: '#fff', border: 'none', borderRadius: '8px', padding: isMobile ? '10px 14px' : '8px 16px', fontSize: isMobile ? '14px' : '13px', cursor: 'pointer', fontWeight: '500' }}>
            + {isMobile ? 'Nueva' : 'Agregar propiedad'}
          </button>
        )}
      </div>

      {/* Filtros */}
      <div style={{ marginBottom: '1.5rem' }}>
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
          <input placeholder='Buscar por título, folio o colonia...' value={buscar} onChange={e => setBuscar(e.target.value)}
            style={{ padding: isMobile ? '10px 12px' : '7px 12px', border: '0.5px solid #ddd', borderRadius: '6px', fontSize: '13px', width: isMobile ? '100%' : '260px', boxSizing: 'border-box' }} />
          <input placeholder='Ciudad...' value={filtroCiudad} onChange={e => setFiltroCiudad(e.target.value)}
            style={{ padding: '7px 12px', border: '0.5px solid #ddd', borderRadius: '6px', fontSize: '13px', width: isMobile ? '100%' : '140px', boxSizing: 'border-box' }} />
          <select value={filtroOperacion} onChange={e => setFiltroOperacion(e.target.value)} style={{ padding: '7px 12px', border: '0.5px solid #ddd', borderRadius: '6px', fontSize: '13px', background: '#fff' }}>
            <option value=''>Operación</option>
            <option value='Venta'>Venta</option>
            <option value='Renta'>Renta</option>
            <option value='RentaTemporal'>Renta temporal</option>
          </select>
          {filtroOperacion && (
            <>
              <input placeholder='Mín.' type='number' value={filtroPrecioMin} onChange={e => setFiltroPrecioMin(e.target.value)} style={{ padding: '7px 12px', border: '0.5px solid #ddd', borderRadius: '6px', fontSize: '13px', width: '90px' }} />
              <input placeholder='Máx.' type='number' value={filtroPrecioMax} onChange={e => setFiltroPrecioMax(e.target.value)} style={{ padding: '7px 12px', border: '0.5px solid #ddd', borderRadius: '6px', fontSize: '13px', width: '90px' }} />
            </>
          )}
          <button onClick={() => setShowFiltros(f => !f)} style={{ padding: '7px 14px', border: '0.5px solid #ddd', borderRadius: '6px', background: showFiltros ? '#4A4A4A' : '#fff', color: showFiltros ? '#fff' : '#333', fontSize: '13px', cursor: 'pointer' }}>
            Tipo {filtroTipo.length > 0 ? `(${filtroTipo.length})` : ''}
          </button>
        </div>
        {showFiltros && (
          <div style={{ marginTop: '10px', padding: '14px', background: '#f9f9f9', borderRadius: '8px', display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)', gap: '14px' }}>
            {Object.entries(TIPOS_PROPIEDAD).map(([categoria, tipos]) => (
              <div key={categoria}>
                <div style={{ fontSize: '11px', fontWeight: '600', color: '#888', marginBottom: '6px' }}>{categoria}</div>
                {tipos.map(t => (
                  <label key={t} style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: '#333', marginBottom: '4px', cursor: 'pointer' }}>
                    <input type='checkbox' checked={filtroTipo.includes(t)} onChange={() => toggleFiltroTipo(t)} />
                    {t}
                  </label>
                ))}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Listado */}
      {loading ? (
        <div style={{ textAlign: 'center', color: '#888', padding: '2rem' }}>Cargando...</div>
      ) : propiedadesFiltradas.length === 0 ? (
        <div style={{ textAlign: 'center', color: '#888', padding: '2rem' }}>Sin propiedades</div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)', gap: '16px' }}>
          {propiedadesFiltradas.map(p => (
            <div key={p.id} style={{ background: '#fff', border: '0.5px solid #e0e0e0', borderRadius: '12px', overflow: 'hidden' }}>
              <div style={{ height: '160px', background: '#f0f0f0' }}>
                {p.portada_url ? <img src={p.portada_url} alt={p.titulo} style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', fontSize: '32px' }}>🏠</div>}
              </div>
              <div style={{ padding: '14px' }}>
                <div style={{ fontSize: '11px', color: '#aaa', marginBottom: '2px' }}>{p.folio}</div>
                <div style={{ fontSize: '13px', fontWeight: '600', color: '#1a1a2e', marginBottom: '6px', lineHeight: '1.3' }}>{p.titulo}</div>
                <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap', marginBottom: '6px' }}>
                  {p.venta_activa && <span style={{ fontSize: '12px', fontWeight: '700', color: '#1a1a2e' }}>{fmt(p.precio_venta)}</span>}
                  {p.renta_activa && <span style={{ fontSize: '11px', color: '#666' }}>{fmt(p.precio_renta)}/mes</span>}
                </div>
                <div style={{ fontSize: '11px', color: '#888', marginBottom: '10px' }}>
                  {[p.recamaras && `${p.recamaras} rec`, p.banos && `${p.banos} baños`, p.construccion_m2 && `${p.construccion_m2} m²`].filter(Boolean).join(' · ')}
                </div>
                <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                  <button onClick={() => setPropiedadCotizar(p)} style={{ flex: 1, padding: '7px', background: '#4A4A4A', color: '#fff', border: 'none', borderRadius: '6px', fontSize: '11px', cursor: 'pointer' }}>Cotizar</button>
                  {puedeAgregar && <button onClick={() => handleEditar(p)} style={{ padding: '7px 10px', background: '#f5f5f5', border: 'none', borderRadius: '6px', fontSize: '11px', cursor: 'pointer' }}>Editar</button>}
                  {(miRol === 'Super Admin') && <button onClick={() => handleEliminar(p.id)} style={{ padding: '7px 10px', background: '#FCEBEB', color: '#A32D2D', border: 'none', borderRadius: '6px', fontSize: '11px', cursor: 'pointer' }}>🗑</button>}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Formulario crear/editar */}
      {showForm && puedeAgregar && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, overflowY: 'auto' }}>
          <div style={{ background: '#fff', minHeight: '100vh', padding: isMobile ? '1rem' : '2rem', maxWidth: '900px', margin: '0 auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem', paddingBottom: '1rem', borderBottom: '0.5px solid #f0f0f0', position: 'sticky', top: 0, background: '#fff', zIndex: 10 }}>
              <h3 style={{ fontSize: '17px', fontWeight: '500' }}>{editando ? `Editar (${form.folio})` : 'Agregar propiedad'}</h3>
              <button onClick={() => { setShowForm(false); setEditando(null); }} style={{ background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer', color: '#888' }}>×</button>
            </div>

            <div style={{ display: 'flex', background: '#f5f5f5', borderRadius: '10px', padding: '4px', marginBottom: '20px', gap: '4px', overflowX: 'auto' }}>
              {[['general', 'General'], ['detalles', 'Detalles'], ['ubicacion', 'Ubicación'], ['caracteristicas', 'Características'], ['fotos', 'Fotos']].map(([key, label]) => (
                <button key={key} onClick={() => setTabForm(key)}
                  style={{ flex: 1, padding: '8px 10px', border: 'none', borderRadius: '7px', cursor: 'pointer', fontSize: '12px', fontWeight: tabForm === key ? '600' : '400', background: tabForm === key ? '#4A4A4A' : 'transparent', color: tabForm === key ? '#fff' : '#888', whiteSpace: 'nowrap' }}>
                  {label}
                </button>
              ))}
            </div>

            {tabForm === 'general' && (
              <div>
                <div style={{ marginBottom: '14px' }}>
                  <label style={{ fontSize: '11px', color: '#888', display: 'block', marginBottom: '4px' }}>Tipo de propiedad *</label>
                  <select value={form.tipo_propiedad} onChange={e => setForm({ ...form, tipo_propiedad: e.target.value })}
                    style={{ width: '100%', padding: '9px 10px', border: '0.5px solid #ddd', borderRadius: '6px', fontSize: '13px', background: '#fff' }}>
                    <option value=''>Elige un tipo de propiedad</option>
                    {Object.entries(TIPOS_PROPIEDAD).map(([cat, tipos]) => (
                      <optgroup key={cat} label={cat}>{tipos.map(t => <option key={t} value={t}>{t}</option>)}</optgroup>
                    ))}
                  </select>
                </div>
                {inp('Título del anuncio *', 'titulo')}
                <div style={{ marginBottom: '14px' }}>
                  <label style={{ fontSize: '11px', color: '#888', display: 'block', marginBottom: '4px' }}>Descripción</label>
                  <textarea value={form.descripcion} onChange={e => setForm({ ...form, descripcion: e.target.value })} rows={5}
                    style={{ width: '100%', padding: '9px 10px', border: '0.5px solid #ddd', borderRadius: '6px', fontSize: '13px', boxSizing: 'border-box', resize: 'vertical' }} />
                </div>

                <div style={{ fontSize: '14px', fontWeight: '600', color: '#1a1a2e', margin: '1.5rem 0 1rem' }}>Operación y precio</div>
                {[['venta_activa', 'precio_venta', 'Venta'], ['renta_activa', 'precio_renta', 'Renta'], ['renta_temporal_activa', 'precio_renta_temporal', 'Renta temporal']].map(([keyActivo, keyPrecio, label]) => (
                  <div key={keyActivo} style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
                    <div onClick={() => setForm({ ...form, [keyActivo]: !form[keyActivo] })}
                      style={{ width: '44px', height: '24px', borderRadius: '12px', background: form[keyActivo] ? '#4A4A4A' : '#ccc', cursor: 'pointer', position: 'relative', flexShrink: 0 }}>
                      <span style={{ width: '20px', height: '20px', borderRadius: '50%', background: '#fff', position: 'absolute', top: '2px', left: form[keyActivo] ? '22px' : '2px', transition: 'left 0.15s' }} />
                    </div>
                    <span style={{ fontSize: '13px', width: '110px' }}>{label}</span>
                    {form[keyActivo] && (
                      <input type='number' placeholder='Precio' value={form[keyPrecio]} onChange={e => setForm({ ...form, [keyPrecio]: e.target.value })}
                        style={{ flex: 1, padding: '8px 10px', border: '0.5px solid #ddd', borderRadius: '6px', fontSize: '13px' }} />
                    )}
                  </div>
                ))}
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginTop: '14px' }}>
                  <span style={{ fontSize: '13px' }}>Mostrar precio en el anuncio</span>
                  <div onClick={() => setForm({ ...form, mostrar_precio: !form.mostrar_precio })}
                    style={{ width: '44px', height: '24px', borderRadius: '12px', background: form.mostrar_precio ? '#4A4A4A' : '#ccc', cursor: 'pointer', position: 'relative' }}>
                    <span style={{ width: '20px', height: '20px', borderRadius: '50%', background: '#fff', position: 'absolute', top: '2px', left: form.mostrar_precio ? '22px' : '2px', transition: 'left 0.15s' }} />
                  </div>
                </div>
              </div>
            )}

            {tabForm === 'detalles' && (
              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '0 16px' }}>
                {sel('Recámaras', 'recamaras', OPCIONES_NUM)}
                {sel('Baños', 'banos', OPCIONES_NUM)}
                {sel('Medios baños', 'medios_banos', OPCIONES_NUM)}
                {sel('Estacionamientos', 'estacionamientos', OPCIONES_NUM)}
                {inp('Construcción (m²)', 'construccion_m2', 'number')}
                {inp('Superficie no cubierta (m²)', 'superficie_no_cubierta_m2', 'number')}
                {inp('Terreno (m²)', 'terreno_m2', 'number')}
                {inp('Frente del terreno (m)', 'frente_terreno', 'number')}
                {inp('Fondo del terreno (m)', 'fondo_terreno', 'number')}
                {sel('Año de construcción', 'anio_construccion', OPCIONES_ANIO)}
                {inp('Piso en el que se encuentra', 'piso')}
                {inp('Cantidad de pisos en el edificio', 'pisos_edificio')}
                {sel('Condición', 'condicion', OPCIONES_CONDICION)}
                {sel('Disposición', 'disposicion', OPCIONES_DISPOSICION)}
                {sel('Orientación', 'orientacion', OPCIONES_ORIENTACION)}
                {inp('Mantenimiento mensual', 'mantenimiento_monto', 'number')}
                <div style={{ marginBottom: '14px' }}>
                  <label style={{ fontSize: '11px', color: '#888', display: 'block', marginBottom: '4px' }}>Mantenimiento incluido</label>
                  <div style={{ display: 'flex', gap: '6px' }}>
                    {['No indicado', 'Sí', 'No'].map(o => (
                      <button key={o} onClick={() => setForm({ ...form, mantenimiento_incluido: o })}
                        style={{ flex: 1, padding: '8px', border: `0.5px solid ${form.mantenimiento_incluido === o ? '#4A4A4A' : '#ddd'}`, borderRadius: '6px', background: form.mantenimiento_incluido === o ? '#4A4A4A' : '#fff', color: form.mantenimiento_incluido === o ? '#fff' : '#333', fontSize: '12px', cursor: 'pointer' }}>
                        {o}
                      </button>
                    ))}
                  </div>
                </div>
                {inp('Clave interna', 'clave_interna')}
                {inp('Código de la llave', 'codigo_llave')}
              </div>
            )}

            {tabForm === 'ubicacion' && (
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px' }}>
                  <span style={{ fontSize: '13px' }}>Mostrar ubicación exacta</span>
                  <div onClick={() => setForm({ ...form, mostrar_ubicacion_exacta: !form.mostrar_ubicacion_exacta })}
                    style={{ width: '44px', height: '24px', borderRadius: '12px', background: form.mostrar_ubicacion_exacta ? '#4A4A4A' : '#ccc', cursor: 'pointer', position: 'relative' }}>
                    <span style={{ width: '20px', height: '20px', borderRadius: '50%', background: '#fff', position: 'absolute', top: '2px', left: form.mostrar_ubicacion_exacta ? '22px' : '2px', transition: 'left 0.15s' }} />
                  </div>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '2fr 1fr 1fr', gap: '10px' }}>
                  {inp('Calle', 'calle')}
                  {inp('Número', 'numero')}
                  {inp('Interior', 'interior')}
                </div>
                {inp('Esquina con', 'esquina_con')}
                <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '0 16px' }}>
                  {inp('Código Postal', 'codigo_postal')}
                  {inp('País', 'pais')}
                  {sel('Estado', 'estado', ESTADOS_MX)}
                  {inp('Ciudad', 'ciudad')}
                  {inp('Colonia', 'colonia')}
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                  {inp('Latitud (opcional)', 'latitud', 'number')}
                  {inp('Longitud (opcional)', 'longitud', 'number')}
                </div>
                <div style={{ fontSize: '11px', color: '#aaa', marginTop: '-6px' }}>
                  Nota: por ahora la ubicación se guarda como coordenadas manuales — un mapa interactivo con marcador arrastrable requiere conectar una API de mapas (Google Maps), que todavía no está configurada.
                </div>
              </div>
            )}

            {tabForm === 'caracteristicas' && (
              <div>
                {Object.entries(CARACTERISTICAS).map(([categoria, items]) => (
                  <div key={categoria} style={{ marginBottom: '18px' }}>
                    <div style={{ fontSize: '12px', fontWeight: '600', color: '#888', marginBottom: '8px' }}>{categoria}</div>
                    <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(3, 1fr)', gap: '6px' }}>
                      {items.map(c => (
                        <label key={c} style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: '#333', cursor: 'pointer' }}>
                          <input type='checkbox' checked={form.caracteristicas.includes(c)} onChange={() => handleToggleCaracteristica(c)} />
                          {c}
                        </label>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {tabForm === 'fotos' && (
              <div>
                <div style={{ marginBottom: '20px' }}>
                  <div style={{ fontSize: '13px', fontWeight: '600', marginBottom: '8px' }}>Foto de portada</div>
                  {form.portada_url && <img src={form.portada_url} alt="portada" style={{ width: '200px', height: '130px', objectFit: 'cover', borderRadius: '6px', marginBottom: '8px', display: 'block' }} />}
                  <label style={{ display: 'inline-block', padding: '8px 16px', background: '#4A4A4A', color: '#fff', borderRadius: '6px', fontSize: '13px', cursor: 'pointer' }}>
                    {subiendoFoto ? 'Subiendo...' : form.portada_url ? 'Cambiar portada' : 'Subir portada'}
                    <input type='file' accept='image/*' style={{ display: 'none' }} onChange={handleSubirPortada} />
                  </label>
                </div>
                <div>
                  <div style={{ fontSize: '13px', fontWeight: '600', marginBottom: '8px' }}>
                    Fotos ({form.fotos.length}/{MAX_FOTOS})
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(3, 1fr)', gap: '12px', marginBottom: '14px' }}>
                    {form.fotos.map((foto, i) => (
                      <div key={i} style={{ border: '0.5px solid #e0e0e0', borderRadius: '8px', overflow: 'hidden' }}>
                        <div style={{ position: 'relative' }}>
                          <img src={foto.url} alt="" style={{ width: '100%', height: '110px', objectFit: 'cover', display: 'block' }} />
                          <button onClick={() => handleQuitarFoto(i)} style={{ position: 'absolute', top: '4px', right: '4px', background: 'rgba(0,0,0,0.6)', color: '#fff', border: 'none', borderRadius: '50%', width: '22px', height: '22px', cursor: 'pointer', fontSize: '12px' }}>✕</button>
                        </div>
                        <input placeholder='¿Qué hay en esta foto?' value={foto.descripcion} onChange={e => handleDescripcionFoto(i, e.target.value)}
                          style={{ width: '100%', padding: '6px 8px', border: 'none', borderTop: '0.5px solid #f0f0f0', fontSize: '11px', boxSizing: 'border-box' }} />
                      </div>
                    ))}
                  </div>
                  {form.fotos.length < MAX_FOTOS && (
                    <label style={{ display: 'inline-block', padding: '8px 16px', background: '#f5f5f5', color: '#333', border: '0.5px dashed #ccc', borderRadius: '6px', fontSize: '13px', cursor: 'pointer' }}>
                      {subiendoFoto ? 'Subiendo...' : '+ Agregar foto'}
                      <input type='file' accept='image/*' style={{ display: 'none' }} onChange={handleSubirFoto} />
                    </label>
                  )}
                </div>
                <div style={{ marginTop: '20px' }}>
                  {inp('Link de video (YouTube)', 'video_url')}
                  {inp('Link de tour virtual', 'tour_virtual_url')}
                </div>
              </div>
            )}

            <div style={{ marginTop: '2rem', display: 'flex', justifyContent: 'flex-end', position: 'sticky', bottom: 0, background: '#fff', paddingTop: '1rem', borderTop: '0.5px solid #f0f0f0' }}>
              <button onClick={handleGuardar} disabled={guardando}
                style={{ padding: '12px 32px', background: '#4A4A4A', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '14px', cursor: 'pointer', width: isMobile ? '100%' : 'auto' }}>
                {guardando ? 'Guardando...' : editando ? 'Guardar cambios' : 'Crear y agregar fotos'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}